import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router';
import { Container, Card, Button, Text, Flex, Box } from '../../design-system';
import { matchSorter } from 'match-sorter';
import Alert from '../../helpers/alert';
import { useData } from '../../helpers/data-loader';
import {
  errorMsg,
  checkPermissions,
  catchPlanDetailError,
} from '../../helpers/utils';
import ContainerHeader from '../../components/ContainerHeader';
import ModalForm from '../../components/ModalForm';
import { Table } from '../../components/Table';
import SearchFilter from '../../components/SearchFilter';
import ExportButton from '../../components/ExportButton';
import { Session } from '../../contexts/sessionContext';
import { PERMISSIONS } from '../../globalConstants';
import { CLIENT_COLUMNS, CLIENT_FIELDS } from './constants';

const Clients = () => {
  const navigate = useNavigate();
  const [isModalOpen, toggleModal] = useState(false);
  const [filter, setFilter] = useState('');
  const {
    state: {
      user: { permissions },
    },
  } = useContext(Session);
  const canCreate = checkPermissions(permissions, [PERMISSIONS.CREATE_CLIENTS]);

  const [data, fetchData, loadingClients] = useData('client', {
    errorMsg: errorMsg('clients'),
  }) as [any[], any, any];

  useEffect(() => {
    // Fetch on mount and refetch after the Add Client modal closes;
    // opening the modal doesn't change the data, so skip that transition.
    if (!isModalOpen) {
      fetchData();
    }
  }, [isModalOpen]);

  const onSubmit = async (values: any) => {
    try {
      const {
        data: { id },
      } = await axios.post('client', values);
      navigate(`/clients/edit/${id}`);
      Alert.success('Client successfully created');
    } catch (error) {
      catchPlanDetailError(error?.response);
    }
  };

  const filteredData = useMemo(
    () =>
      matchSorter(data, filter, {
        keys: [
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'name',
          },
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'address',
          },
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'website',
          },
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'poc_title',
          },
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'poc_email',
          },
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'tax_id',
          },
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'focus',
          },
        ],
      }),
    [data, filter],
  );

  const handleFilter = (value: string) => {
    setFilter(value);
  };

  return (
    <Container fluid gutter={50} style={{ width: 'calc(100vw - 220px)' }}>
      <Card mt={30}>
        <ContainerHeader>
          <Text fontWeight={700} fontSize={25}>
            Clients
          </Text>
          {canCreate && (
            <Button.Group>
              <Button type="button" onClick={() => toggleModal(true)}>
                Add
              </Button>
              <Box ml="1rem">
                <ExportButton type="clients" />
              </Box>
            </Button.Group>
          )}
          <ModalForm
            title="Add Client"
            onSubmit={onSubmit}
            isOpen={isModalOpen}
            onClose={() => toggleModal(false)}
            fields={CLIENT_FIELDS}
            fieldWidth={300}
            initialValues={{ country: 'United States' }}
          />
        </ContainerHeader>
        <Flex
          justifyContent="flex-start"
          alignItems="center"
          p="0 36px 18px 26px"
        >
          <Box width={250} justifySelf="flex-end">
            <SearchFilter onFilter={handleFilter} />
          </Box>
        </Flex>
        <Table
          columns={CLIENT_COLUMNS}
          data={filteredData.map((item) => ({
            ...item,
          }))}
          initialSortBy={[{ id: 'name', desc: false }]}
          loading={loadingClients}
        />
      </Card>
    </Container>
  );
};

export default Clients;
